export type WizardAction = 'resume' | 'new'
export type WizardStatus = 'draft' | 'submitted'
export type WizardRenterRole = 'primary' | 'co-applicant'
export type WizardApplicationType =
  | 'Individual'
  | 'Couple'
  | 'Housemates'
  | 'Family'
  | 'CompanyTrust'

export interface IWizardCoApplicant {
  inviteId?: string
  fullName?: string
  mobileNumber?: string
  email?: string
  role?: string
  status?: string
  isMobileVerified?: boolean
  invitedAtUtc?: string
  inviteExpiresAtUtc?: string
  verifiedAtUtc?: string
  revokedAtUtc?: string
  declinedAtUtc?: string
  declineReason?: string
}

export interface IWizardEmployment {
  employmentId?: number
  companyName?: string
  employmentType?: string
  startDate?: string | Date | null
  isStartMonthUnknown?: boolean
  employerName?: string
  employerPhone?: string
  employerEmail?: string
  employerReference?: string
  additionalReferenceName?: string
  additionalReferencePhone?: string
  additionalReferenceEmail?: string
  abn?: string
  businessName?: string
  businessStructure?: string
  businessAddress?: string
  role?: string
  industryType?: string
  uniqueStudentIdentifier?: string
}

export interface IWizardIncome {
  hasEmploymentIncome?: boolean
  hasSelfEmploymentIncome?: boolean
  income?: number
  incomeFrequency?: string
  selfEmploymentIncome?: number
  selfEmploymentIncomeFrequency?: string
  isCentreLink?: boolean
  centreAmount?: number
  centreLinkFrequency?: string
  hasPension?: boolean
  pensionAmount?: number
  pensionFrequency?: string
  hasNdis?: boolean
  ndisAmount?: number
  ndisFrequency?: string
  hasStudentSupport?: boolean
  studentSupportAmount?: number
  studentSupportFrequency?: string
}

export interface IWizardPet {
  id?: number
  type?: string
  breed?: string
  isAssistanceAnimal?: boolean
}

export interface IWizardVehicle {
  id?: number
  makeModel?: string
  registrationNumber?: string
}

export interface IWizardOtherOccupant {
  id?: number
  clientId?: string
  declaredByHouseholdMemberId?: string
  declaredBy?: string
  name?: string
  age?: number | ''
  relationship?: string
}

export interface IWizardDocument {
  documentId?: string
  type?: string
  filename?: string
  contentType?: string
  uploadedAtUtc?: string
  verificationStatus?: string
  storageRef?: string
}

export interface IWizardHouseholdMemberStatus {
  householdMemberId?: string
  dooraCardId?: number
  applicationId?: number
  fullName?: string
  mobileNumber?: string
  email?: string
  role?: string
  membershipStatus?: string
  isMobileVerified?: boolean
  mobileVerifiedAtUtc?: string
  isCardComplete?: boolean
  cardCompletedAtUtc?: string
  canSubmitHousehold?: boolean
  missingRequiredFields?: string[]
}

export interface IWizardHouseholdInviteStatus {
  inviteId?: string
  householdInviteId?: string
  fullName?: string
  mobileNumber?: string
  email?: string
  role?: string
  status?: string
  inviteStatus?: string
  isMobileVerified?: boolean
  invitedAtUtc?: string
  inviteExpiresAtUtc?: string
  verifiedAtUtc?: string
  otpExpiresAt?: string
  declinedAtUtc?: string
  declineReason?: string
  relationship?: string
  canResend?: boolean
  missingRequiredFields?: string[]
}

export interface IWizardSubmissionBlockReason {
  householdInviteId?: string
  householdMemberId?: string
  dooraCardId?: number
  role?: string
  inviteStatus?: string
  membershipStatus?: string
  fullName?: string
  mobileNumber?: string
  isMobileVerified?: boolean
  mobileVerifiedAtUtc?: string
  isCardComplete?: boolean
  cardCompletedAtUtc?: string
  reasonCodes?: string[]
  reasonMessages?: string[]
}

export interface IWizardSubmissionGate {
  isBlocked?: boolean
  blockCode?: string
  message?: string
  includedHouseholdMemberIds?: string[]
  blockReasons?: IWizardSubmissionBlockReason[]
}

export interface IWizardCardReadiness {
  isComplete: boolean
  canFinalize?: boolean
  missingRequirementCodes: string[]
  missingRequirementMessages: string[]
  documentReadiness?: {
    isIdentificationTypeSelected: boolean
    requiredTypes: string[]
    missingTypes: string[]
    deferredTypes?: string[]
  }
}

export interface IWizardHouseholdEligibilityBlocker {
  code: string
  message: string
  householdMemberId?: string
  householdInviteId?: string
}

export interface IWizardHouseholdEligibility {
  canSubmit: boolean
  blockingReasons: IWizardHouseholdEligibilityBlocker[]
  warnings?: Array<{code: string; message: string; householdInviteId?: string}>
}

export interface IWizardReviewSummary {
  applicationId?: number
  applicationContextId?: number
  sessionId?: string
  applicantName?: string
  mobileNumber?: string
  email?: string
  applicationType?: string
  status?: string
  hasDashboardAccess?: boolean
  isMobileVerified?: boolean
  lastSavedAtUtc?: string
  coApplicantCount?: number
  employmentCount?: number
  petCount?: number
  vehicleCount?: number
  documentCount?: number
  incompleteUploadCount?: number
  otherOccupantCount?: number
  areRequiredCoApplicantsComplete?: boolean
  coApplicantStatuses?: IWizardReviewCoApplicantStatus[]
  missingCriticalFields: string[]
  renterRole?: string
  householdId?: number
  activeMembers?: IWizardHouseholdMemberStatus[]
  pendingInvites?: IWizardHouseholdInviteStatus[]
  submissionGate?: IWizardSubmissionGate
  cardReadiness?: IWizardCardReadiness
  householdEligibility?: IWizardHouseholdEligibility
}

export interface IWizardSubmissionLink {
  submissionId?: string
  secureViewUrl?: string
  sentAtUtc?: string
  expiresAtUtc?: string
  status?: string
  pmEmail?: string
}

export interface IWizardReviewCoApplicantStatus {
  inviteId?: string
  fullName?: string
  mobileNumber?: string
  role?: string
  status?: string
  isMobileVerified?: boolean
  coApplicantApplicationId?: number
  hasLinkedApplication?: boolean
  isCompleted?: boolean
  invitedAtUtc?: string | Date
  inviteExpiresAtUtc?: string | Date
  otpExpiresAt?: string | Date
  declinedAtUtc?: string | Date
  declineReason?: string
  missingRequiredFields?: string[]
}

export interface IApplicationFormData {
  sessionId?: string
  applicationId?: number
  stepKey?: string
  blockNotVerifiedCoApplicant: boolean
  status?: WizardStatus
  renterRole?: WizardRenterRole
  coApplicantInviteId?: string
  primaryApplicantName?: string

  fullName?: string
  firstName?: string
  lastName?: string
  mobileNumber?: string
  australianStatus?: string
  email?: string
  dateOfBirth?: string | Date | null
  termsAccepted?: boolean
  consentSharingAccepted?: boolean

  isIdentityVerified?: boolean
  isMobileVerified?: boolean
  mobileVerifiedAtUtc?: string
  lastSavedAtUtc?: string

  applicationType?: WizardApplicationType
  companyTrustAbnAcn?: string
  companyTrustDirectorId?: string
  companyTrustLetterOfAuthorityDocumentId?: string

  emergencyContactName?: string
  emergencyContactPhone?: string
  emergencyContactRelationship?: string

  moveInAsap?: boolean
  moveInPreferredDate?: string
  preferredLeaseTerm?: string
  rentWeeklyBudgetMin?: number
  rentWeeklyBudgetMax?: number

  previousRentalHistory?: boolean
  previousPropertyManager?: string
  previousPropertyManagerPhone?: string
  previousRentWeekly?: number
  previousRentDuration?: string
  rentalLedgerUploadLater?: boolean
  rentalHistoryReasonForLeaving?: string
  bondFullyRefundedAtLastAddress?: boolean

  identificationType?: string
  isVisaHolder?: boolean
  isInternationalStudent?: boolean
  incomeUploadLaterTypes: string[]

  retentionPreference?: number
  retentionPreferenceText?: string
  retentionSelectedAtUtc?: string
  // Present only until this tab's explicit radio choice has been saved.
  retentionPreferenceSelection?: number
  auditTransparencyAcknowledged?: boolean

  coApplicants: IWizardCoApplicant[]
  employment?: IWizardEmployment
  income?: IWizardIncome
  pets: IWizardPet[]
  vehicles: IWizardVehicle[]
  otherOccupants: IWizardOtherOccupant[]
  otherOccupantBaseline?: IWizardOtherOccupant[]
  removedOtherOccupants?: IWizardOtherOccupant[]
  documents: IWizardDocument[]

  hasPets?: boolean
  hasVehicles?: boolean
  hasOtherOccupants?: boolean

  action?: WizardAction
  existingApplicationId?: number
  otpCode?: string
  otpCodeValid?: boolean
  otpRetryAfterSeconds?: number
  otpLockedUntilUtc?: string
  otpRequiresNewCode?: boolean
  pmEmail?: string
  submissionLinks: IWizardSubmissionLink[]
  latestSubmissionUrl?: string
  latestSubmissionExpiresAtUtc?: string
  reviewSummary?: IWizardReviewSummary | null
}

const normalizeAbnValue = (value?: string | null): string | undefined => {
  if (value == null) {
    return undefined
  }

  return value?.replace(/\D/g, '').slice(0, 11)
}

const normalizeEmployment = (employment: IWizardEmployment): IWizardEmployment => ({
  employmentId: employment.employmentId,
  companyName: employment.companyName,
  employmentType: employment.employmentType,
  startDate: employment.startDate,
  isStartMonthUnknown: employment.isStartMonthUnknown,
  employerName: employment.employerName,
  employerPhone: employment.employerPhone,
  employerEmail: employment.employerEmail,
  employerReference: employment.employerReference,
  additionalReferenceName: employment.additionalReferenceName,
  additionalReferencePhone: employment.additionalReferencePhone,
  additionalReferenceEmail: employment.additionalReferenceEmail,
  abn: normalizeAbnValue(employment.abn),
  businessName: employment.businessName,
  businessStructure: employment.businessStructure,
  businessAddress: employment.businessAddress,
  role: employment.role,
  industryType: employment.industryType,
  uniqueStudentIdentifier: employment.uniqueStudentIdentifier,
})

const normalizeIncomeFrequency = (frequency?: string): string | undefined => {
  const normalized = frequency?.trim().toLowerCase()
  return normalized || undefined
}

const normalizeIncome = (income: IWizardIncome): IWizardIncome => ({
  hasEmploymentIncome: income.hasEmploymentIncome,
  hasSelfEmploymentIncome: income.hasSelfEmploymentIncome,
  income: income.income,
  incomeFrequency: normalizeIncomeFrequency(income.incomeFrequency),
  selfEmploymentIncome: income.selfEmploymentIncome,
  selfEmploymentIncomeFrequency: normalizeIncomeFrequency(income.selfEmploymentIncomeFrequency),
  isCentreLink: income.isCentreLink,
  centreAmount: income.centreAmount,
  centreLinkFrequency: normalizeIncomeFrequency(income.centreLinkFrequency),
  hasPension: income.hasPension,
  pensionAmount: income.pensionAmount,
  pensionFrequency: normalizeIncomeFrequency(income.pensionFrequency),
  hasNdis: income.hasNdis,
  ndisAmount: income.ndisAmount,
  ndisFrequency: normalizeIncomeFrequency(income.ndisFrequency),
  hasStudentSupport: income.hasStudentSupport,
  studentSupportAmount: income.studentSupportAmount,
  studentSupportFrequency: normalizeIncomeFrequency(income.studentSupportFrequency),
})

export const createEmptyApplicationFormData = (): IApplicationFormData => ({
  status: 'draft',
  renterRole: 'primary',
  blockNotVerifiedCoApplicant: false,
  termsAccepted: false,
  consentSharingAccepted: false,
  isIdentityVerified: false,
  isMobileVerified: false,
  otpCodeValid: false,
  rentalLedgerUploadLater: false,
  coApplicants: [],
  employment: undefined,
  income: undefined,
  pets: [],
  vehicles: [],
  otherOccupants: [],
  documents: [],
  incomeUploadLaterTypes: [],
  submissionLinks: [],
  reviewSummary: null,
})

export const normalizeApplicationFormData = (
  data?: Partial<IApplicationFormData>
): IApplicationFormData => {
  const formData = data ?? {}
  const fallback = createEmptyApplicationFormData()

  return {
    ...fallback,
    ...formData,
    coApplicants: formData.coApplicants ?? fallback.coApplicants,
    employment: formData.employment
      ? normalizeEmployment(formData.employment)
      : fallback.employment,
    income: formData.income ? normalizeIncome(formData.income) : fallback.income,
    pets: formData.pets ?? fallback.pets,
    vehicles: formData.vehicles ?? fallback.vehicles,
    otherOccupants: formData.otherOccupants ?? fallback.otherOccupants,
    documents: formData.documents ?? fallback.documents,
    incomeUploadLaterTypes: formData.incomeUploadLaterTypes ?? fallback.incomeUploadLaterTypes,
    submissionLinks: formData.submissionLinks ?? fallback.submissionLinks,
    reviewSummary: formData.reviewSummary ?? fallback.reviewSummary,
    renterRole: formData.renterRole ?? fallback.renterRole,
  }
}
